import React, { useState, useEffect, useRef } from "react";
import {
	Button,
	Typography,
	Paper,
	Container,
	Grid,
	Box,
	Chip,
	CardContent,
	Alert,
	Snackbar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormControlLabel,
	Checkbox,
	CircularProgress,
	RadioGroup,
	Radio,
	FormControl,
	FormLabel,
	Divider,
} from "@mui/material";
import {
	VideoCall,
	AutoAwesome,
	Videocam,
	ScreenShare,
	Mic,
	Stop,
	Download,
} from "@mui/icons-material";

const TutorialVideo = () => {
	const [quota, setQuota] = useState(0);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
	const [isRecording, setIsRecording] = useState(false);
	const [selectedInputs, setSelectedInputs] = useState({
		camera: false,
		screen: false,
		audio: false,
	});
	const [recordingTime, setRecordingTime] = useState(0);
	const [recordedBlob, setRecordedBlob] = useState(null);
	const [selectedQuality, setSelectedQuality] = useState("low"); // low, medium, high

	// Quality presets
	const qualityPresets = {
		low: {
			label: "Lowest",
			bitrate: 1000000, // 1 Mbps
			resolution: { width: 854, height: 480 },
			fps: 15,
		},
		medium: {
			label: "Medium",
			bitrate: 2500000, // 2.5 Mbps
			resolution: { width: 1280, height: 720 },
			fps: 20,
		},
		high: {
			label: "Highest",
			bitrate: 5000000, // 5 Mbps
			resolution: { width: 1920, height: 1080 },
			fps: 30,
		},
	};

	const mediaRecorderRef = useRef(null);
	const recordedChunksRef = useRef([]);
	const cameraStreamRef = useRef(null);
	const screenStreamRef = useRef(null);
	const audioStreamRef = useRef(null);
	const timerRef = useRef(null);
	const canvasRef = useRef(null);
	const cameraVideoRef = useRef(null);
	const screenVideoRef = useRef(null);
	const animationFrameRef = useRef(null);
	const isDrawingRef = useRef(false);

	useEffect(() => {
		getUserInfo();
		return () => {
			// Cleanup on unmount
			stopAllStreams();
			if (timerRef.current) {
				clearInterval(timerRef.current);
			}
		};
	}, []);

	const getUserInfo = async () => {
		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "getUserInfo",
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const user_info = await response.json();
			setQuota(user_info[0].quota);
			localStorage.setItem("user_info", JSON.stringify(user_info[0]));
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to fetch user information",
				severity: "error",
			});
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	const handleInputChange = (input) => {
		setSelectedInputs((prev) => ({
			...prev,
			[input]: !prev[input],
		}));
	};

	const stopAllStreams = () => {
		// Stop drawing
		isDrawingRef.current = false;
		
		// Stop animation frame
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		// Stop media streams
		if (cameraStreamRef.current) {
			cameraStreamRef.current.getTracks().forEach((track) => track.stop());
			cameraStreamRef.current = null;
		}
		if (screenStreamRef.current) {
			screenStreamRef.current.getTracks().forEach((track) => track.stop());
			screenStreamRef.current = null;
		}
		if (audioStreamRef.current) {
			audioStreamRef.current.getTracks().forEach((track) => track.stop());
			audioStreamRef.current = null;
		}

		// Clean up video elements
		if (cameraVideoRef.current) {
			cameraVideoRef.current.srcObject = null;
			cameraVideoRef.current.pause();
		}
		if (screenVideoRef.current) {
			screenVideoRef.current.srcObject = null;
			screenVideoRef.current.pause();
		}
	};

	const startRecording = async () => {
		const hasSelection =
			selectedInputs.camera || selectedInputs.screen || selectedInputs.audio;

		if (!hasSelection) {
			setSnackbar({
				open: true,
				message: "Please select at least one input source",
				severity: "error",
			});
			return;
		}

		// Require screen if camera is selected (for picture-in-picture effect)
		if (selectedInputs.camera && !selectedInputs.screen) {
			setSnackbar({
				open: true,
				message: "Screen recording is required when using camera (camera will appear as inset)",
				severity: "error",
			});
			return;
		}

		try {
			recordedChunksRef.current = [];
			
			// Get quality preset
			const quality = qualityPresets[selectedQuality];
			
			// Create canvas for compositing with quality-based resolution
			const canvas = document.createElement("canvas");
			canvas.width = quality.resolution.width;
			canvas.height = quality.resolution.height;
			canvasRef.current = canvas;
			const ctx = canvas.getContext("2d");

			// Create video elements (hidden)
			const cameraVideo = document.createElement("video");
			cameraVideo.autoplay = true;
			cameraVideo.playsInline = true;
			cameraVideoRef.current = cameraVideo;

			const screenVideo = document.createElement("video");
			screenVideo.autoplay = true;
			screenVideo.playsInline = true;
			screenVideoRef.current = screenVideo;

			// Get screen stream (required for main feed)
			let screenStream = null;
			if (selectedInputs.screen) {
				// Don't request audio from screen sharing - we'll get it separately from microphone
				// Screen sharing audio is unreliable and depends on browser/user selection
				screenStream = await navigator.mediaDevices.getDisplayMedia({
					video: true,
					audio: false,
				});
				screenStreamRef.current = screenStream;
				screenVideo.srcObject = screenStream;
				
				// Wait for video metadata and ensure it's playing
				await new Promise((resolve, reject) => {
					screenVideo.onloadedmetadata = async () => {
						// Canvas size is already set based on quality preset
						// Screen video will be scaled to fit the canvas
						
						// Play the video
						try {
							await screenVideo.play();
							// Wait for video to be ready
							const checkReady = () => {
								if (screenVideo.readyState >= 2) {
									resolve();
								} else {
									setTimeout(checkReady, 100);
								}
							};
							checkReady();
						} catch (error) {
							reject(error);
						}
					};
					screenVideo.onerror = reject;
				});
			}

			// Get camera stream (for inset)
			let cameraStream = null;
			if (selectedInputs.camera) {
				cameraStream = await navigator.mediaDevices.getUserMedia({
					video: true,
					audio: false, // We'll get audio separately
				});
				cameraStreamRef.current = cameraStream;
				cameraVideo.srcObject = cameraStream;
				
				// Wait for camera video metadata and ensure it's playing
				await new Promise((resolve, reject) => {
					cameraVideo.onloadedmetadata = async () => {
						try {
							await cameraVideo.play();
							// Wait for video to be ready
							const checkReady = () => {
								if (cameraVideo.readyState >= 2) {
									resolve();
								} else {
									setTimeout(checkReady, 100);
								}
							};
							checkReady();
						} catch (error) {
							reject(error);
						}
					};
					cameraVideo.onerror = reject;
				});
			}

			// Get audio stream
			let audioTracks = [];
			if (selectedInputs.audio) {
				// Always get a separate microphone stream for audio
				// This is more reliable than screen sharing audio which depends on browser/user selection
				try {
					const audioStream = await navigator.mediaDevices.getUserMedia({
						audio: {
							echoCancellation: true,
							noiseSuppression: true,
							autoGainControl: true,
						},
					});
					audioStreamRef.current = audioStream;
					const tracks = audioStream.getAudioTracks();
					audioTracks.push(...tracks);
					console.log("Microphone audio tracks obtained:", tracks.length);
				} catch (error) {
					console.error("Failed to get audio stream:", error);
					setSnackbar({
						open: true,
						message: "Failed to access microphone. Please check permissions.",
						severity: "error",
					});
				}
			}

			// Function to draw frames on canvas
			const drawFrame = () => {
				if (!isDrawingRef.current) return;

				// Clear canvas with black background
				ctx.fillStyle = "#000000";
				ctx.fillRect(0, 0, canvas.width, canvas.height);

				// Draw screen as main feed (full size)
				if (screenVideo && screenVideo.readyState >= 2) {
					try {
						const videoWidth = screenVideo.videoWidth || canvas.width;
						const videoHeight = screenVideo.videoHeight || canvas.height;
						
						if (videoWidth > 0 && videoHeight > 0) {
							ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height);
						}
					} catch (error) {
						console.error("Error drawing screen video:", error);
					}
				}

				// Draw camera as inset (bottom-right corner)
				if (cameraVideo && cameraVideo.readyState >= 2) {
					try {
						const videoWidth = cameraVideo.videoWidth;
						const videoHeight = cameraVideo.videoHeight;
						
						if (videoWidth > 0 && videoHeight > 0) {
							const insetWidth = canvas.width * 0.25; // 25% of canvas width
							const insetHeight = (insetWidth * videoHeight) / videoWidth;
							const x = canvas.width - insetWidth - 20; // 20px margin
							const y = canvas.height - insetHeight - 20; // 20px margin

							// Draw border around camera inset
							ctx.strokeStyle = "#ffffff";
							ctx.lineWidth = 3;
							ctx.strokeRect(x - 2, y - 2, insetWidth + 4, insetHeight + 4);

							// Draw camera video
							ctx.drawImage(cameraVideo, x, y, insetWidth, insetHeight);
						}
					} catch (error) {
						console.error("Error drawing camera video:", error);
					}
				}

				// Continue drawing
				animationFrameRef.current = requestAnimationFrame(drawFrame);
			};

			// Get canvas stream first with quality-based frame rate
			const canvasStream = canvas.captureStream(quality.fps);
			
			// Start drawing frames before recording
			isDrawingRef.current = true;
			drawFrame();

			// Wait a bit to ensure frames are being drawn
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Validate and enable audio tracks
			const validAudioTracks = audioTracks.filter(
				(track) => track && track.readyState === "live"
			);
			
			// Ensure all audio tracks are enabled
			validAudioTracks.forEach((track) => {
				if (track) {
					track.enabled = true;
				}
			});
			
			console.log("Audio tracks:", {
				total: audioTracks.length,
				valid: validAudioTracks.length,
				tracks: validAudioTracks.map((t) => ({
					id: t.id,
					kind: t.kind,
					label: t.label,
					enabled: t.enabled,
					readyState: t.readyState,
					muted: t.muted,
				})),
			});

			// Combine canvas video with audio tracks
			const finalStream = new MediaStream([
				...canvasStream.getVideoTracks(),
				...validAudioTracks,
			]);
			
			console.log("Final stream tracks:", {
				video: finalStream.getVideoTracks().length,
				audio: finalStream.getAudioTracks().length,
			});

			// Create MediaRecorder with audio support
			// Try different codec options that support audio
			const codecOptions = [
				"video/webm;codecs=vp9,opus",
				"video/webm;codecs=vp8,opus",
				"video/webm;codecs=h264,opus",
				"video/webm",
			];

			let selectedMimeType = "video/webm";
			for (const mimeType of codecOptions) {
				if (MediaRecorder.isTypeSupported(mimeType)) {
					selectedMimeType = mimeType;
					console.log("Using codec:", mimeType);
					break;
				}
			}

			const options = {
				mimeType: selectedMimeType,
				videoBitsPerSecond: quality.bitrate,
			};
			
			// Warn if no audio tracks but audio was requested
			if (selectedInputs.audio && validAudioTracks.length === 0) {
				console.warn("Audio was requested but no valid audio tracks found");
				setSnackbar({
					open: true,
					message: "Warning: No audio tracks available. Recording will continue without audio.",
					severity: "warning",
				});
			}

			const mediaRecorder = new MediaRecorder(finalStream, options);
			mediaRecorderRef.current = mediaRecorder;

			mediaRecorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) {
					recordedChunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = () => {
				// Stop drawing
				isDrawingRef.current = false;
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current);
					animationFrameRef.current = null;
				}
				
				const blob = new Blob(recordedChunksRef.current, {
					type: "video/webm",
				});
				setRecordedBlob(blob);
				stopAllStreams();
			};

			// Start recording with timeslice to ensure regular data capture
			mediaRecorder.start(100); // Capture data every 100ms
			setIsRecording(true);
			setRecordingDialogOpen(false);
			setRecordingTime(0);

			// Start timer
			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);

			setSnackbar({
				open: true,
				message: "Recording started",
				severity: "success",
			});
		} catch (error) {
			console.error("Error starting recording:", error);
			setSnackbar({
				open: true,
				message: `Failed to start recording: ${error.message}`,
				severity: "error",
			});
			stopAllStreams();
		}
	};

	const stopRecording = () => {
		if (mediaRecorderRef.current && isRecording) {
			mediaRecorderRef.current.stop();
			setIsRecording(false);
			if (timerRef.current) {
				clearInterval(timerRef.current);
				timerRef.current = null;
			}
			setSnackbar({
				open: true,
				message: "Recording stopped. File is ready to download.",
				severity: "success",
			});
		}
	};

	const downloadRecording = () => {
		if (recordedBlob) {
			const url = URL.createObjectURL(recordedBlob);
			const a = document.createElement("a");
			a.href = url;
			const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
			a.download = `tutorial-video-${timestamp}.webm`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
			setRecordedBlob(null);
			setRecordingTime(0);
			setSnackbar({
				open: true,
				message: "File downloaded successfully",
				severity: "success",
			});
		}
	};

	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	const handleOpenDialog = () => {
		setRecordingDialogOpen(true);
		setSelectedInputs({
			camera: false,
			screen: false,
			audio: false,
		});
		setSelectedQuality("low"); // Reset to default quality
	};

	const handleCloseDialog = () => {
		if (!isRecording) {
			setRecordingDialogOpen(false);
		}
	};

	return (
		<Container maxWidth="lg" className="py-8">
			<Paper elevation={3} className="rounded-lg overflow-hidden">
				<Box className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 text-white">
					<Typography variant="h4" className="font-bold text-center">
						Create Avatar Video
					</Typography>
					<Typography
						variant="subtitle1"
						className="text-center mt-2"
					>
						Generate videos with AI avatars in multiple languages
					</Typography>
				</Box>

				<CardContent className="p-6 bg-white">
					<Box className="mb-6">
						<Grid container spacing={2} className="mb-4">
							<Grid item xs={12} md={6}>
								<Chip
									icon={<AutoAwesome />}
									label={`Available Credits: ${quota}/20`}
									color="primary"
									variant="outlined"
									className="px-4 py-3"
								/>
							</Grid>
						</Grid>

						<Box className="mt-6 flex flex-col items-center gap-4">
							{!isRecording ? (
								<Button
									variant="contained"
									color="primary"
									size="large"
									startIcon={<VideoCall />}
									onClick={handleOpenDialog}
									className="bg-purple-600 hover:bg-purple-700 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
								>
									Record Tutorial Video
								</Button>
							) : (
								<Box className="flex flex-col items-center gap-4 w-full">
									<Box className="flex items-center gap-4">
										<CircularProgress size={24} className="text-purple-600" />
										<Typography variant="h6" className="font-bold text-purple-600">
											Recording: {formatTime(recordingTime)}
										</Typography>
									</Box>
									<Button
										variant="contained"
										color="error"
										size="large"
										startIcon={<Stop />}
										onClick={stopRecording}
										className="bg-red-600 hover:bg-red-700 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
									>
										Stop Recording
									</Button>
								</Box>
							)}

							{recordedBlob && !isRecording && (
								<Button
									variant="contained"
									color="success"
									size="large"
									startIcon={<Download />}
									onClick={downloadRecording}
									className="bg-green-600 hover:bg-green-700 font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
								>
									Download Recording
								</Button>
							)}
						</Box>
					</Box>
				</CardContent>
			</Paper>

			{/* Recording Options Dialog */}
			<Dialog
				open={recordingDialogOpen}
				onClose={handleCloseDialog}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<Typography variant="h6" className="font-bold">
						Select Recording Sources
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Box className="flex flex-col gap-4 mt-2">
						<FormControlLabel
							control={
								<Checkbox
									checked={selectedInputs.camera}
									onChange={() => handleInputChange("camera")}
									icon={<Videocam />}
									checkedIcon={<Videocam />}
								/>
							}
							label={
								<Box className="flex items-center gap-2">
									<Videocam className="text-blue-600" />
									<Typography variant="body1" className="font-medium">
										Camera
									</Typography>
								</Box>
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={selectedInputs.screen}
									onChange={() => handleInputChange("screen")}
									icon={<ScreenShare />}
									checkedIcon={<ScreenShare />}
								/>
							}
							label={
								<Box className="flex items-center gap-2">
									<ScreenShare className="text-green-600" />
									<Typography variant="body1" className="font-medium">
										Screen
									</Typography>
								</Box>
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={selectedInputs.audio}
									onChange={() => handleInputChange("audio")}
									icon={<Mic />}
									checkedIcon={<Mic />}
								/>
							}
							label={
								<Box className="flex items-center gap-2">
									<Mic className="text-purple-600" />
									<Typography variant="body1" className="font-medium">
										Audio
									</Typography>
								</Box>
							}
						/>

						<Divider className="my-2" />

						{/* Quality Selection */}
						<FormControl component="fieldset" className="mt-2">
							<FormLabel component="legend">
								<Typography variant="subtitle1" className="font-bold">
									Video Quality
								</Typography>
							</FormLabel>
							<RadioGroup
								value={selectedQuality}
								onChange={(e) => setSelectedQuality(e.target.value)}
								className="mt-2"
							>
								<FormControlLabel
									value="low"
									control={<Radio />}
									label={
										<Box>
											<Typography variant="body1" className="font-medium">
												Lowest - ~1 Mbps
											</Typography>
											<Typography variant="body2" className="text-gray-500">
												854x480 @ 15fps (Smallest file size)
											</Typography>
										</Box>
									}
								/>
								<FormControlLabel
									value="medium"
									control={<Radio />}
									label={
										<Box>
											<Typography variant="body1" className="font-medium">
												Medium - ~2.5 Mbps
											</Typography>
											<Typography variant="body2" className="text-gray-500">
												1280x720 @ 20fps (Balanced)
											</Typography>
										</Box>
									}
								/>
								<FormControlLabel
									value="high"
									control={<Radio />}
									label={
										<Box>
											<Typography variant="body1" className="font-medium">
												Highest - ~5 Mbps
											</Typography>
											<Typography variant="body2" className="text-gray-500">
												1920x1080 @ 30fps (Best quality, largest file)
											</Typography>
										</Box>
									}
								/>
							</RadioGroup>
						</FormControl>

						<Typography variant="body2" className="text-gray-500 mt-2">
							Select one or more sources to record. If camera is selected, screen
							recording is required (camera will appear as a small inset overlay).
							Audio will be included automatically with screen if selected.
						</Typography>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog} color="secondary">
						Cancel
					</Button>
					<Button
						onClick={startRecording}
						variant="contained"
						color="primary"
						startIcon={<VideoCall />}
						disabled={
							(!selectedInputs.camera &&
								!selectedInputs.screen &&
								!selectedInputs.audio) ||
							(selectedInputs.camera && !selectedInputs.screen)
						}
					>
						Start Recording
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default TutorialVideo; 